import React, {
  Dispatch,
  SetStateAction,
  TextareaHTMLAttributes,
  useEffect,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import { RootState, useAppDispatch, useAppSelector } from '../../store'
import LeftSide from '../Mentor/Mentee/LeftSide'
import { Mentee, User } from '../../models/User'
import { asyncGetMentees, updateMenteeInfo } from '../../store/mainSlice'
import { Job as JobData } from '../../models/Job'
import Job from '../Common/Job'
import { JobRecommend } from '../../models/JobRecommend'
import axios from 'axios'
import { UserGroupDto } from '../../models/Dto/UserGroupDto'
import TextAreaBox from '../Common/TextAreaBox'
import { axiosGet, axiosPost } from '../Common/Axios'
import { useSelector } from 'react-redux'
import { ApiResult } from '../../models/Enums'

interface OtherProps {
  setOpen?: Dispatch<SetStateAction<boolean>>
  job: JobData
}

const RecommandForm = (props: OtherProps) => {
  const [selectedGroup, setSelectedGroup] = useState(0)
  const [selectedUser, setSelectedUser] = useState(0)
  const [userGroups, setUserGroups] = useState<UserGroupDto[]>([])
  const [mentees, setMentees] = useState<Mentee[]>([])
  const [mentee, setMentee] = useState<Mentee | null>(null)
  const [updateJobRecomend, setUpdateJobRecomend] = useState<boolean>(false)
  const [students, setStudents] = useState<User[]>([])

  const recommendTextareaRef = useRef<HTMLTextAreaElement>(null)

  const dispatch = useAppDispatch()

  const menteeGroups = useAppSelector(
    (state: RootState) => state.main.menteeGroups
  )
  const me = useAppSelector((state: RootState) => state.user.me)
  const menteesLoadingStatus = useAppSelector(
    (state: RootState) => state.main.loadingStatus
  )

  const selectGroupId = useAppSelector(
    (state: RootState) => state.user.selectGroupId
  )

  const loadStudents = async () => {
    const results = await axiosGet('/api/user/get_students/' + selectGroupId)
    if (results.apiResult !== ApiResult.SUCCESS) {
      console.log('get_students error', results.message)
      return
    }
    setStudents(results.data)
  }

  // 시작시 멘티를 로딩한다
  useEffect(() => {
    initialLoadMentees()
  }, [])

  // 최초 menteeGroup Load(없을 경우만 loading)
  const initialLoadMentees = async () => {
    if (menteeGroups.length == 0) {
      await dispatch(asyncGetMentees(me.id))
    }
  }

  // menteeGroups가 Loading되면 menteeGroups을 UserGroup에 복사
  useEffect(() => {
    // 최초에만 셋팅한다. 멘티 정보를 수정할때 store의 값을 바꾸지만
    // 그때 다시 렌더링 할 필요는 없다.
    if (userGroups.length === 0) {
      setUserGroups([...menteeGroups])
    }
  }, [menteeGroups])

  useEffect(() => {
    if (selectedGroup < userGroups.length) {
      setMentees(userGroups[selectedGroup].users)

      if (selectedUser < userGroups[selectedGroup].users.length) {
        setMentee(userGroups[selectedGroup].users[selectedUser])
      }
    }
    if (userGroups.length > 0) {
      setUpdateJobRecomend(true)
    }
  }, [userGroups])

  useEffect(() => {
    if (selectedGroup < userGroups.length) {
      setSelectedUser(0)
      setMentees(userGroups[selectedGroup].users)
    }
  }, [selectedGroup])

  useEffect(() => {
    setMentee(mentees[selectedUser])
  }, [selectedUser])

  // updateJobRecommend가 셋팅되면 JobRecommend를 다시 불러온다
  useEffect(() => {
    if (updateJobRecomend === false) {
      return
    }
    loadJobRecommends()
  }, [updateJobRecomend])

  const loadJobRecommends = async () => {
    const jobId = props.job.id
    const mentorUserId = me.id
    const payload = { jobId, mentorUserId }

    const data = await axiosPost('/api/mentor/get_recommends', payload)

    const jobRecommends: JobRecommend[] = data

    setUserGroups(
      userGroups.map((group: UserGroupDto) => {
        const newGroup = { ...group }

        newGroup.users = group.users.map((user: Mentee) => {
          const index = jobRecommends.findIndex(
            (recommend: JobRecommend) => user.mentee.id === recommend.recvUserId
          )
          if (index > -1) {
            return {
              ...user,
              checked: true,
              recommendContent: jobRecommends[index].recommendContent,
            }
          } else {
            return {
              ...user,
              checked: false,
              recommendContent: '',
            }
          }
        })
        return newGroup
      })
    )
  }

  // 추천 클릭시 [userGroups 변경]
  const handleCheckboxChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { checked } = e.target
    if (mentee === null) {
      return
    }

    const payload: JobRecommend = {
      id: 0,
      jobId: props.job.id,
      isFromMentor: true,
      sendUserId: me.id,
      recvUserId: mentee.mentee.id,
      recommendContent: mentee.recommendContent,
    }

    //const isChecked = e.target.checked

    const apiUrl = checked
      ? '/api/mentor/insert_recommend'
      : '/api/mentor/delete_recommend'

    const data = await axiosPost(apiUrl, payload)

    if (data === 'success') {
      setUserGroups(
        userGroups.map((group: UserGroupDto) => {
          const newGroup = { ...group }
          newGroup.users = group.users.map((user: Mentee) => {
            if (user.mentee.id === payload.recvUserId) {
              if (checked) {
                return {
                  ...user,
                  checked,
                }
              } else {
                return {
                  ...user,
                  checked,
                  recommendContent: '',
                }
              }
            } else {
              return user
            }
          })
          return newGroup
        })
      )
    }
  }

  useEffect(() => {
    if (
      recommendTextareaRef !== null &&
      recommendTextareaRef.current !== null
    ) {
      recommendTextareaRef.current.focus()
      recommendTextareaRef.current.scrollIntoView()
    }
  }, [mentee?.checked])

  // 추천이유 적용 클릭시 [selector 변경]
  const applyRecommend = async (value: string) => {
    if (mentee === null) {
      return
    }

    const payload: JobRecommend = {
      id: 0,
      jobId: props.job.id,
      isFromMentor: true,
      sendUserId: me.id,
      recvUserId: mentee.mentee.id,
      recommendContent: value,
    }

    const data = await axiosPost('/api/mentor/update_recommend', payload)
    if (data === 'success') {
      setUserGroups(
        userGroups.map((group: UserGroupDto) => {
          const newGroup = { ...group }
          newGroup.users = group.users.map((user: Mentee) => {
            if (user.mentee.id === payload.recvUserId) {
              return {
                ...user,
                recommendContent: payload.recommendContent,
              }
            } else {
              return user
            }
          })
          return newGroup
        })
      )
    }
    console.log('data', data)
  }

  // 멘티 정보 적용 클릭시
  const applyMenteeInfo = async (value: string) => {
    if (mentee === null) {
      return
    }

    const payload = {
      mentorUserId: me.id,
      menteeUserId: mentee.mentee.id,
      menteeInfo: value,
    }

    const data = await axiosPost('/api/mentor/update_mentee_info', payload)
    if (data === 'success') {
      // menteeInfo는 Global하게 적용되기 때문에 updateMenteeInfo를 호출해야 한다
      dispatch(
        updateMenteeInfo({
          userGroupId: userGroups[selectedGroup].userGroup.id,
          userId: mentee.mentee.id,
          menteeInfo: value,
        })
      )
      setUserGroups(
        userGroups.map((group: UserGroupDto) => {
          const newGroup = { ...group }
          newGroup.users = group.users.map((user: Mentee) => {
            if (user.mentee.id === payload.menteeUserId) {
              return {
                ...user,
                menteeInfo: payload.menteeInfo,
              }
            } else {
              return user
            }
          })
          return newGroup
        })
      )
    }
  }

  // mentees가 변경되면 mentee를 첫번째 user로 다시 셋팅한다
  useEffect(() => {
    if (
      userGroups.length > selectedGroup &&
      userGroups[selectedGroup].users.length > selectedUser
    ) {
      setMentee(mentees[selectedUser])
    } else {
      setMentee(null)
    }
  }, [mentees])

  return (
    <Container>
      <Title>직업 추천</Title>
      <Content>
        <LeftSide
          // TODO: FIXIT
          students={students}
          selectedStudent={null}
          setSelectedStudent={() => {
            console.log('object')
          }}
          menteeGroups={userGroups}
          mentees={mentees}
          selectedGroup={selectedGroup}
          selectGroup={setSelectedGroup}
          selectedUser={selectedUser}
          selectUser={setSelectedUser}
          userListHeight="600px"
        />
        <Main>
          <Job
            id={props.job.id}
            name={props.job.name}
            // image={process.env.REACT_APP_API_SERVER + props.job.photo}
            image={props.job.photo}
            description={props.job.overview}
          />

          {mentee && mentee.mentee && (
            <>
              <UserName>{mentee.mentee.username}</UserName>

              <UserInfo>
                <div>멘티 정보</div>
                <TextAreaBox
                  updateId={mentee.mentee.id}
                  value={mentee.menteeInfo}
                  placeholder="멘티에 대해 메모해주세요"
                  updateValue={applyMenteeInfo}
                  isShowButtons={true}
                />
              </UserInfo>

              <Check>
                <input
                  id="recommendBtn"
                  type="checkbox"
                  onChange={handleCheckboxChange}
                  checked={mentee.checked}
                />
                <label htmlFor="recommendBtn"> 추천 </label>
              </Check>
              {mentee.checked && (
                <UserInfo>
                  <div>추천이유</div>
                  <TextAreaBox
                    updateId={mentee.mentee.id}
                    value={mentee.recommendContent}
                    placeholder="추천하는 이유를 알려주세요."
                    updateValue={applyRecommend}
                    isShowButtons={true}
                  />
                </UserInfo>
              )}
            </>
          )}
        </Main>
      </Content>
    </Container>
  )
}

const Container = styled.div`
  @media (max-width: 768px) {
    width: calc(80vw);
    max-height: 80vh;
  }
`

const Content = styled.div`
  display: grid;
  grid-template-areas: 'leftside main';
  grid-template-columns: minmax(280px, 3fr) minmax(700px, 12fr);
  column-gap: 10px;
  grid-template-rows: 100%;
  width: 1100px;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    padding: 0;
    margin: 0 0 80px 0;
    width: 100%;
  }
`
const Check = styled.div`
  padding: 30px 0 10px;
  font-size: 1.05rem;
`
const UserName = styled.div`
  font-size: 1.2rem;
  padding: 10px 0;
  font-weight: 600;
  color: #333;
`
const UserInfo = styled.div`
  margin: 20px 20 20px;

  div:first-child {
    font-weight: 600;
    color: #999;
    padding-bottom: 5px;
  }
`

const Title = styled.div`
  margin-bottom: 20px;
  font-size: 1.5rem;
  text-align: center;
  color: #666;
`

const Main = styled.div`
  padding: 5px 20px;
  @media (max-width: 768px) {
    padding: 10px 0;
  }
`

export default RecommandForm
